import { InformationCircleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Collapsible, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ApiClinicLeadPayorChoices, ApiClinicListingVerificationStatusChoices } from "@api/graphql/types-and-hooks";
import { ClinicLead, usePublicClinicLeadRetrieve } from "@api/rest/types-and-hooks";
import { ClaimClinicForm } from "@pages/Clinic/ClaimClinicForm";

export enum ClaimView {
  LEAD_SUMMARY = "lead summary",
  VERIFY_FORM = "verify form",
}

export const ClaimClinic: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<ClaimView>(ClaimView.LEAD_SUMMARY);

  const { data, isLoading, isError } = usePublicClinicLeadRetrieve(
    { lead_id: leadId ?? "" },
    { query: { enabled: !!leadId } },
  );

  const leadData: ClinicLead | undefined = data?.status === 200 ? data.data : undefined;

  const getInsuranceProvider = (providedInsurance: string | null): string => {
    if (!providedInsurance) return "Not provided";

    const isValidPayor = (Object.values(ApiClinicLeadPayorChoices) as string[]).includes(providedInsurance);

    if (isValidPayor) {
      return "Insurance provided (plan hidden)";
    }
    return "Not provided";
  };

  if (isLoading) {
    return (
      <div className="flex flex-col w-full items-center justify-center my-4 md:my-8 min-h-[200px]">
        <Paragraph size="medium" colorType="secondary">
          Loading...
        </Paragraph>
      </div>
    );
  }

  if (isError || !leadData) {
    return (
      <div className="flex flex-col w-full items-center justify-center my-4 md:my-8 min-h-[200px] px-4">
        <Paragraph className="text-xl! md:text-2xl!" size="large" colorType="primary" displayType="loud">
          Lead not found
        </Paragraph>
        <Paragraph size="medium" colorType="secondary" className="mt-2 text-center">
          This lead may have been removed or the link may be incorrect.
        </Paragraph>
      </div>
    );
  }

  return (
    <div>
      {view == ClaimView.LEAD_SUMMARY ? (
        <div className="flex flex-col w-full items-center my-4 md:my-8">
          <div className="w-full max-w-2xl px-4 sm:px-6">
            <div className="flex flex-col items-center">
              <Paragraph className="text-xl! md:text-2xl!" size="large" colorType="primary" displayType="loud">
                A family is interested {leadData.clinic_listing.name}
              </Paragraph>
              <Paragraph size="medium">
                To protect privacy, lead details are hidden until you log in and claim this clinic.
              </Paragraph>
            </div>

            <div className="flex flex-col gap-2 m-2 p-4 sm:m-4 sm:p-6 md:p-8 border rounded-lg border-limestone-100 bg-white">
              <Paragraph className="text-xl" displayType="loud" colorType="primary">
                Lead Summary
              </Paragraph>

              {leadData.requested_service_types.length > 0 && (
                <div>
                  <Paragraph size="small">Service Requested</Paragraph>
                  <Paragraph colorType="primary" size="medium">
                    {leadData.requested_service_types.join(", ")}
                  </Paragraph>
                </div>
              )}

              {leadData.location && (
                <div>
                  <Paragraph size="small">Location</Paragraph>
                  <Paragraph colorType="primary" size="medium">
                    {leadData.location} (zip: *****)
                  </Paragraph>
                </div>
              )}

              {leadData.requested_modality_types.length > 0 && (
                <div>
                  <Paragraph size="small">Modality</Paragraph>
                  <Paragraph colorType="primary" size="medium">
                    {leadData.requested_modality_types.join(", ")}
                  </Paragraph>
                </div>
              )}

              <div>
                <Paragraph size="small">Insurance</Paragraph>
                <Paragraph colorType="primary" size="medium">
                  {getInsuranceProvider(leadData.payor)}
                </Paragraph>
              </div>

              <div>
                <Paragraph size="small">Submitted</Paragraph>
                <Paragraph colorType="primary" size="medium">
                  {format(new Date(leadData.created_date), "EEE, MMM d yyyy h:mma")}
                </Paragraph>
              </div>

              <Paragraph>Locked Information</Paragraph>
              <div className="flex flex-col gap-4 p-4 border rounded-lg border-limestone-300 bg-limestone-50">
                <div className="flex flex-row gap-2 items-center">
                  <LockClosedIcon className="w-6 text-text-secondary" />
                  <Paragraph>Parent contact info</Paragraph>
                </div>

                <div className="flex flex-row gap-2 items-center">
                  <LockClosedIcon className="w-6 text-text-secondary" />
                  <Paragraph>Child details and age</Paragraph>
                </div>

                <div className="flex flex-row gap-2 items-center">
                  <LockClosedIcon className="w-6 text-text-secondary" />
                  <Paragraph>Notes and preferences</Paragraph>
                </div>

                <div className="flex flex-row gap-2 items-center">
                  <LockClosedIcon className="w-6 text-text-secondary" />
                  <Paragraph>Insurance plan details</Paragraph>
                </div>
              </div>

              {leadData.clinic_listing.verification_status === ApiClinicListingVerificationStatusChoices.Pending && (
                <div className="flex flex-row gap-2 p-4 rounded-lg bg-blue-50">
                  <InformationCircleIcon className="text-blue-800 size-6 shrink-0" />
                  <Paragraph className="text-blue-800!">
                    A claim has already been submitted for this clinic. If this is your clinic, our team will contact
                    you to complete the verification process.
                  </Paragraph>
                </div>
              )}

              <div className="flex flex-row items-center gap-4 mt-6 justify-end">
                {leadData.clinic_listing.verification_status !== ApiClinicListingVerificationStatusChoices.Verified && (
                  <Button text="This isn't my clinic" appearance="secondary" onClick={() => navigate("/support")} />
                )}
                {leadData.clinic_listing.verification_status ===
                  ApiClinicListingVerificationStatusChoices.Unverified && (
                  <Button
                    text="Claim your clinic"
                    appearance="primary"
                    onClick={() => setView(ClaimView.VERIFY_FORM)}
                  />
                )}
                {leadData.clinic_listing.verification_status === ApiClinicListingVerificationStatusChoices.Pending && (
                  <Button
                    text="View pending claim"
                    appearance="primary"
                    onClick={() => setView(ClaimView.VERIFY_FORM)}
                  />
                )}
                {leadData.clinic_listing.verification_status === ApiClinicListingVerificationStatusChoices.Verified && (
                  <Button
                    text="Log in to view"
                    appearance="primary"
                    onClick={() => (window.location.href = `/leads/${leadId}?public=true`)}
                  />
                )}
              </div>
            </div>

            <div className="px-4 mt-8">
              <div className="flex flex-col gap-2 max-w-xl">
                <Paragraph className="text-2xl!" colorType="primary" displayType="loud">
                  What is Frontera?
                </Paragraph>
                <Paragraph className="text-base!" colorType="primary">
                  Frontera helps families find care based on needs, location, insurance, and availability.
                </Paragraph>
                <Paragraph className="text-base!" colorType="primary">
                  We created this listing using publicly available information to help families discover your services.
                </Paragraph>
                <Paragraph className="text-base!" colorType="primary">
                  Claim your clinic to manage your listing, update your information, and receive leads from families
                  seeking care.
                </Paragraph>
              </div>

              <div className="border border-limestone-200 my-4"></div>

              <div className="flex flex-col max-w-xl my-2">
                <Paragraph size="medium" colorType="primary" displayType="loud">
                  Frequently Asked Questions
                </Paragraph>

                <div className="border rounded-md border-limestone-100 bg-white px-3 py-2 sm:px-4 sm:py-2 my-2">
                  <Collapsible
                    headerIsClickable={true}
                    headerContent={
                      <Paragraph size="small" colorType="primary" displayType="loud">
                        How did you get my clinic info?
                      </Paragraph>
                    }
                    bodyContent={
                      <div className="flex flex-col pt-2 min-w-0">
                        <Paragraph className="wrap-break-word!" size="small" colorType="primary">
                          We created this listing using publicly available information from business directories,
                          healthcare provider databases, and clinic websites. We compile this information to help
                          families find appropriate care.
                        </Paragraph>
                      </div>
                    }
                  />
                </div>

                <div className="border rounded-md border-limestone-100 bg-white px-3 py-2 sm:px-4 sm:py-2 my-2">
                  <Collapsible
                    headerIsClickable={true}
                    headerContent={
                      <Paragraph size="small" colorType="primary" displayType="loud">
                        Do I have to pay?
                      </Paragraph>
                    }
                    bodyContent={
                      <div className="flex flex-col pt-2 min-w-0">
                        <Paragraph className="wrap-break-word!" size="small" colorType="primary">
                          No, claiming your clinic and receiving leads is completely free. There are no hidden fees or
                          subscription costs. We believe in connecting families with care providers without financial
                          barriers.
                        </Paragraph>
                      </div>
                    }
                  />
                </div>

                <div className="border rounded-md border-limestone-100 bg-white px-3 py-2 sm:px-4 sm:py-2 my-2">
                  <Collapsible
                    headerIsClickable={true}
                    headerContent={
                      <Paragraph size="small" colorType="primary" displayType="loud">
                        What happens after I contact the family?
                      </Paragraph>
                    }
                    bodyContent={
                      <div className="flex flex-col pt-2 min-w-0">
                        <Paragraph className="wrap-break-word!" size="small" colorType="primary">
                          All follow-up communication happens directly between you and the family, outside of Frontera.
                          We simply facilitate the initial connection. You'll manage scheduling, intake, and all other
                          details directly.
                        </Paragraph>
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ClaimClinicForm leadDetails={leadData} setView={setView} />
      )}
    </div>
  );
};
